'use client';

// IMPORTANT: Create storage bucket in Supabase Dashboard -> Storage:
// Bucket name: auditions
// Public: false
// RLS policy:
// CREATE POLICY "Users can upload their own auditions" ON storage.objects
//   FOR INSERT TO authenticated
//   WITH CHECK (bucket_id = 'auditions' AND auth.uid()::text = (storage.foldername(name))[1]);
// CREATE POLICY "Users can view their own auditions" ON storage.objects
//   FOR SELECT USING (bucket_id = 'auditions' AND auth.uid()::text = (storage.foldername(name))[1]);
//
// IMPORTANT: Run in Supabase SQL Editor if not already done:
// ALTER TABLE job_applications ADD COLUMN IF NOT EXISTS character_id text;
// ALTER TABLE job_applications ADD COLUMN IF NOT EXISTS character_name text;
// ALTER TABLE job_applications ADD COLUMN IF NOT EXISTS audio_url text;
// ALTER TABLE job_applications ADD COLUMN IF NOT EXISTS audio_link text;
// ALTER TABLE job_applications ADD COLUMN IF NOT EXISTS is_video boolean DEFAULT false;
//
// RLS policies:
// CREATE POLICY "Users can submit applications" ON job_applications
//   FOR INSERT TO authenticated WITH CHECK (auth.uid() = applicant_id);
// CREATE POLICY "Users can view their own applications" ON job_applications
//   FOR SELECT USING (auth.uid() = applicant_id);
// CREATE POLICY "Posters can view applications for their jobs" ON job_applications
//   FOR SELECT USING (
//     EXISTS (SELECT 1 FROM job_postings WHERE id = job_id AND posted_by = auth.uid())
//   );

import { useState, useCallback, useEffect } from 'react';
import { getJobStatus } from '@/lib/jobStatus';
import { X, Upload, Link as LinkIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/useToast';
import { createClient } from '@/lib/supabase/browser';
import type { AuditionCharacter } from '@/types/jobs';
import LinkPreview, { detectLink } from '@/components/LinkPreview';

const AUDIO_ACCEPT = '.wav,.mp3,.ogg,.flac';
const VIDEO_ACCEPT = '.mp4,.mov,.webm';
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

export interface AuditionModalProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
  jobTitle: string;
  characters: AuditionCharacter[];
  initialCharacterId?: string;
  allowMultiCharacter: boolean;
  submittedCharacterIds: Set<string>;
  onSuccess: (characterIds: string[]) => void;
}

export default function AuditionModal({
  open,
  onClose,
  jobId,
  jobTitle,
  characters,
  initialCharacterId,
  allowMultiCharacter,
  submittedCharacterIds,
  onSuccess
}: AuditionModalProps) {
  const { toast } = useToast();
  const [tab, setTab] = useState<'upload' | 'link'>('upload');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(characters.length ? [characters[0].id] : []));
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [link, setLink] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  const [coverNote, setCoverNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    if (open && characters.length) {
      const preferred = initialCharacterId
        ? characters.find((c) => c.id === initialCharacterId && !submittedCharacterIds.has(c.id))
        : null;
      const firstSelectable = preferred || characters.find((c) => !submittedCharacterIds.has(c.id));
      setSelectedIds(new Set(firstSelectable ? [firstSelectable.id] : []));
      setFile(null);
      setLink('');
      setFileError(null);
      setLinkError(null);
      setSubmitError(null);
    }
  }, [open, characters, initialCharacterId, submittedCharacterIds]);

  const allowVideo = characters.some(c => c.allow_video);
  const acceptStr = allowVideo
    ? `${AUDIO_ACCEPT},${VIDEO_ACCEPT}`
    : AUDIO_ACCEPT;

  const toggleCharacter = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        if (next.size <= 1) return prev;
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const canSubmit =
    selectedIds.size > 0 &&
    (tab === 'upload' ? !!file : !!link && link.startsWith('https://'));

  const validateLink = (value: string) => {
    if (!value.trim()) return false;
    return value.trim().startsWith('https://');
  };

  const getAllowedExtensions = () =>
    (allowVideo ? `${AUDIO_ACCEPT},${VIDEO_ACCEPT}` : AUDIO_ACCEPT)
      .split(',')
      .map((ext) => ext.trim().toLowerCase());

  const isAllowedFile = (f: File) => {
    const ext = '.' + (f.name.split('.').pop() || '').toLowerCase();
    return getAllowedExtensions().includes(ext);
  };

  const handleFileChange = (f: File | null) => {
    setFile(f);
    setFileError(null);
    if (f) {
      if (!isAllowedFile(f)) {
        setFileError(`Invalid file type. Allowed: ${allowVideo ? '.wav, .mp3, .ogg, .flac, .mp4, .mov' : '.wav, .mp3, .ogg, .flac'}`);
        return;
      }
      if (f.size > MAX_FILE_SIZE) {
        setFileError('File size must be 100MB or less');
      }
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const f = e.dataTransfer.files?.[0];
      if (!f) return;
      if (!isAllowedFile(f)) {
        setFileError('Invalid file type. Allowed: ' + (allowVideo ? 'audio and video' : 'audio only'));
        return;
      }
      if (f.size > MAX_FILE_SIZE) {
        setFileError('File size must be 100MB or less');
        return;
      }
      handleFileChange(f);
    },
    [allowVideo]
  );

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitError(null);
    setSubmitting(true);

    try {
      const supabase = createClient();
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('You must be logged in.');

      const { data: jobRow } = await supabase
        .from('job_postings')
        .select('is_filled, is_archived, post_removal_date')
        .eq('id', jobId)
        .single();
      const { canApply, tooltip } = getJobStatus(jobRow);
      if (!canApply) throw new Error(tooltip ?? 'This posting is not accepting applications.');

      const toSubmit = Array.from(selectedIds)
        .map(id => characters.find(c => c.id === id))
        .filter((c): c is AuditionCharacter => Boolean(c) && !submittedCharacterIds.has((c as AuditionCharacter).id));

      const applications: Array<{
        job_id: string;
        applicant_id: string;
        character_id: string;
        character_name: string;
        audio_url: string | null;
        audio_link: string | null;
        is_video: boolean;
        cover_letter: string | null;
        status: 'pending';
      }> = [];

      if (toSubmit.length === 0) {
        throw new Error('You have already submitted for the selected character(s).');
      }

      if (tab === 'upload' && file) {
        const isVideo = ['.mp4', '.mov', '.webm'].some(ext => file.name.toLowerCase().endsWith(ext));
        const sanitized = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const bucket = 'auditions';

        for (const ch of toSubmit) {
          const fileName = `${Date.now()}-${ch.id}-${sanitized}`;
          const path = `${user.id}/${jobId}/${ch.id}/${fileName}`;
          const { error: uploadError } = await supabase.storage
            .from(bucket)
            .upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type });
          if (uploadError) {
            console.error(`[Upload:${bucket}]`, JSON.stringify(uploadError));
            if (uploadError.message?.includes('Bucket not found')) {
              throw new Error('This upload feature is not yet configured. Please contact support@indiestryvoice.com');
            }
            if (uploadError.message?.includes('Payload too large')) {
              throw new Error('File is too large. Please use a file under 100MB.');
            }
            if (uploadError.message?.includes('not allowed') || uploadError.message?.includes('policy')) {
              throw new Error('Upload permission denied. Please sign out and back in.');
            }
            throw new Error(`Upload failed: ${uploadError.message}`);
          }

          // auditions bucket is private — use signed URL (1-year expiry)
          const { data: signedData, error: signedError } = await supabase.storage
            .from(bucket)
            .createSignedUrl(path, 60 * 60 * 24 * 365);
          if (signedError || !signedData?.signedUrl) {
            throw new Error('Failed to get signed URL for uploaded file');
          }

          applications.push({
            job_id: jobId,
            applicant_id: user.id,
            character_id: ch.id,
            character_name: ch.character_name,
            audio_url: signedData.signedUrl,
            audio_link: null,
            is_video: isVideo,
            cover_letter: coverNote.trim() || null,
            status: 'pending',
          });
        }
      } else {
        const url = link.trim();
        if (!validateLink(url)) throw new Error('Please enter a valid https:// link');
        for (const ch of toSubmit) {
          applications.push({
            job_id: jobId,
            applicant_id: user.id,
            character_id: ch.id,
            character_name: ch.character_name,
            audio_url: null,
            audio_link: url,
            is_video: false,
            cover_letter: coverNote.trim() || null,
            status: 'pending',
          });
        }
      }

      const { error: insertError } = await supabase.from('job_applications').insert(applications);
      if (insertError) {
        console.error('[Audition] Insert error:', JSON.stringify(insertError));
        if (insertError.code === '23505') {
          throw new Error('You have already applied for this character.');
        }
        throw new Error(`Submission failed: ${insertError.message}`);
      }

      toast({
        title: 'Audition submitted',
        description: 'Your audition has been submitted!',
        variant: 'default'
      });
      onSuccess(toSubmit.map(c => c.id));
      onClose();
      setFile(null);
      setLink('');
      setCoverNote('');
      setSubmitError(null);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const singleCharacter = characters.length === 1 && !allowMultiCharacter;
  const headerTitle = singleCharacter
    ? `Audition for ${characters[0]?.character_name || 'Character'}`
    : `Audition for ${jobTitle}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-surface-2 backdrop-blur border border-border-subtle rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-subtle flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-xl font-bold text-accent">
              Indiestry
            </span>
            <h2 className="text-lg font-semibold text-text-primary truncate">{headerTitle}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-surface-3 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Character selector (multi only) */}
          {allowMultiCharacter && characters.length > 1 && (
            <div className="space-y-2">
              <Label className="text-text-primary">Select character(s) to audition for</Label>
              <div className="space-y-2">
                {characters.map(ch => (
                  <label
                    key={ch.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      submittedCharacterIds.has(ch.id)
                        ? 'bg-surface-3 border-border-subtle opacity-60 cursor-not-allowed'
                        : 'bg-surface-3 border-border-default cursor-pointer hover:bg-surface-2'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(ch.id)}
                      disabled={submittedCharacterIds.has(ch.id)}
                      onChange={() => toggleCharacter(ch.id)}
                      className="rounded border-border-default bg-surface-3 accent-accent"
                    />
                    <span className="text-text-primary font-medium">
                      {ch.character_name}
                      {submittedCharacterIds.has(ch.id) ? ' (Already submitted)' : ''}
                    </span>
                  </label>
                ))}
              </div>
              {selectedIds.size === 0 && (
                <p className="text-status-busy text-sm">Select at least one character.</p>
              )}
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 border-b border-border-subtle pb-2">
            <button
              type="button"
              onClick={() => setTab('upload')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                tab === 'upload'
                  ? 'bg-surface-3 text-text-primary'
                  : 'text-text-tertiary hover:text-text-primary'
              }`}
            >
              <span className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Upload File
              </span>
            </button>
            <button
              type="button"
              onClick={() => setTab('link')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                tab === 'link'
                  ? 'bg-surface-3 text-text-primary'
                  : 'text-text-tertiary hover:text-text-primary'
              }`}
            >
              <span className="flex items-center gap-2">
                <LinkIcon className="w-4 h-4" />
                Paste Link
              </span>
            </button>
          </div>

          {tab === 'upload' && (
            <div className="space-y-2">
              <Label className="text-text-primary">Audition file</Label>
              <div
                onDragOver={e => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  dragOver ? 'border-accent bg-accent-soft' : 'border-border-default bg-surface-3'
                }`}
              >
                <input
                  type="file"
                  accept={acceptStr}
                  onChange={e => handleFileChange(e.target.files?.[0] || null)}
                  className="hidden"
                  id="audition-file"
                />
                <label htmlFor="audition-file" className="cursor-pointer block">
                  <Upload className="w-10 h-10 text-text-tertiary mx-auto mb-2" />
                  <p className="text-text-secondary">
                    Drag and drop or click to upload
                  </p>
                  <p className="text-text-tertiary text-sm mt-1">
                    {allowVideo ? 'Audio: WAV, MP3, OGG, FLAC. Video: MP4, MOV, WEBM.' : 'WAV, MP3, OGG, FLAC.'} Max 100MB.
                  </p>
                </label>
              </div>
              {file && (
                <p className="text-text-secondary text-sm">
                  {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </p>
              )}
              {fileError && <p className="text-status-busy text-sm">{fileError}</p>}
            </div>
          )}

          {tab === 'link' && (
            <div className="space-y-2">
              <Label htmlFor="audition-link" className="text-text-primary">
                Link to your audition
              </Label>
              <Input
                id="audition-link"
                type="url"
                value={link}
                onChange={e => {
                  setLink(e.target.value);
                  setLinkError(null);
                }}
                placeholder="https:// (Google Drive, Dropbox, YouTube, etc.)"
                className="bg-surface-3 border-border-default rounded-lg text-text-primary placeholder:text-text-tertiary"
              />
              {linkError && <p className="text-status-busy text-sm">{linkError}</p>}
              {link && !validateLink(link) && (
                <p className="text-status-busy text-sm">Link must start with https://</p>
              )}
              {link && validateLink(link) && (
                <div className="mt-2">
                  <LinkPreview url={link} />
                  {detectLink(link).type === 'youtube' && (
                    <p className="text-xs text-text-tertiary mt-1">
                      Tip: Set your YouTube video to &ldquo;Unlisted&rdquo; so only people with the link can view it.
                      Avoid &ldquo;Private&rdquo; — private videos can&apos;t be previewed by the job poster.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Cover note */}
          <div className="space-y-2">
            <Label htmlFor="cover-note" className="text-text-primary">Cover note (optional)</Label>
            <Textarea
              id="cover-note"
              value={coverNote}
              onChange={e => setCoverNote(e.target.value)}
              placeholder="Add a note to the job poster (optional)"
              className="bg-surface-3 border-border-default rounded-lg text-text-primary placeholder:text-text-tertiary min-h-[80px]"
            />
          </div>

          {submitError && (
            <p className="text-status-busy text-sm">{submitError}</p>
          )}
        </div>

        <div className="p-4 border-t border-border-subtle flex-shrink-0">
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="w-full py-3 px-6 bg-accent hover:bg-accent-hover text-text-on-accent rounded-lg font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {uploadProgress > 0 ? `Uploading ${uploadProgress}%...` : 'Submitting...'}
              </span>
            ) : 'Submit Audition'}
          </button>
        </div>
      </div>
    </div>
  );
}
